/**
 * Glossary content loader.
 *
 * Mirrors `src/content/articles/index.ts`: markdown files with YAML
 * frontmatter, eagerly globbed at build time, validated hard so a malformed
 * entry fails the build rather than shipping a broken page.
 *
 * Each term is a standalone indexable page. The quality bar is deliberate —
 * every entry must carry a precise definition, Anter-specific runtime
 * behaviour, and at least one docs deep link. Templated stubs that restate the
 * title are what trigger Google's scaled-content policy, and that assessment is
 * site-wide, so a thin glossary would drag down the pages that already work.
 */
import { parse as parseYaml } from "yaml";

type GlossaryFrontmatter = {
  term: string;
  slug: string;
  /** One sentence. Doubles as the meta description and the `DefinedTerm` description. */
  shortDefinition: string;
  category: string;
  aliases?: string[];
  related?: string[];
  docsRefs?: { label: string; href: string }[];
  updatedAt: string;
  draft?: boolean;
};

export type GlossaryDocsRef = { label: string; href: string };

export type GlossaryTerm = {
  term: string;
  slug: string;
  shortDefinition: string;
  category: string;
  aliases: string[];
  related: string[];
  docsRefs: GlossaryDocsRef[];
  updatedAt: string;
  draft: boolean;
  content: string;
};

const termModules = import.meta.glob("./terms/*.md", {
  eager: true,
  import: "default",
  query: "?raw",
}) as Record<string, string>;

function splitFrontmatter(raw: string, filePath: string): { frontmatter: string; content: string } {
  const normalized = raw.replace(/\r\n/g, "\n");
  const match = normalized.match(/^---\n([\s\S]*?)\n---\n?([\s\S]*)$/);
  if (!match || !match[1]) {
    throw new Error(`Glossary term "${filePath}" is missing a valid frontmatter block.`);
  }
  return { frontmatter: match[1], content: (match[2] ?? "").trim() };
}

function assertString(value: unknown, field: string, filePath: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new Error(`Glossary term "${filePath}" has invalid "${field}" value.`);
  }
  return value.trim();
}

function assertDate(value: unknown, field: string, filePath: string): string {
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString().split("T")[0] ?? value.toISOString();
  }
  const parsed = assertString(value, field, filePath);
  if (Number.isNaN(Date.parse(parsed))) {
    throw new Error(`Glossary term "${filePath}" has invalid date in "${field}".`);
  }
  return parsed;
}

function stringArray(value: unknown): string[] {
  return Array.isArray(value)
    ? value.filter((v): v is string => typeof v === "string" && v.trim().length > 0)
    : [];
}

function buildTerms(): GlossaryTerm[] {
  const slugSet = new Set<string>();

  const terms = Object.entries(termModules).map(([filePath, raw]) => {
    const { frontmatter, content } = splitFrontmatter(raw, filePath);
    const parsed = parseYaml(frontmatter) as GlossaryFrontmatter | null;
    if (!parsed || typeof parsed !== "object") {
      throw new Error(`Glossary term "${filePath}" frontmatter could not be parsed.`);
    }

    const slug = assertString(parsed.slug, "slug", filePath);
    if (slugSet.has(slug)) {
      throw new Error(`Duplicate glossary slug "${slug}" found in "${filePath}".`);
    }
    slugSet.add(slug);

    // Guard the quality bar in code, not in a style guide nobody reads.
    if (content.length < 600) {
      throw new Error(
        `Glossary term "${filePath}" is too thin (${content.length} chars). ` +
          `Write a real entry or delete it — stubs hurt the whole site.`
      );
    }

    const docsRefs = Array.isArray(parsed.docsRefs)
      ? parsed.docsRefs.filter(
          (ref): ref is GlossaryDocsRef =>
            !!ref && typeof ref.label === "string" && typeof ref.href === "string"
        )
      : [];
    if (docsRefs.length === 0) {
      throw new Error(`Glossary term "${filePath}" must link at least one docs page.`);
    }

    return {
      term: assertString(parsed.term, "term", filePath),
      slug,
      shortDefinition: assertString(parsed.shortDefinition, "shortDefinition", filePath),
      category: assertString(parsed.category, "category", filePath),
      aliases: stringArray(parsed.aliases),
      related: stringArray(parsed.related),
      docsRefs,
      updatedAt: assertDate(parsed.updatedAt, "updatedAt", filePath),
      draft: parsed.draft === true,
      content,
    } satisfies GlossaryTerm;
  });

  return terms.sort((a, b) => a.term.localeCompare(b.term));
}

export const allGlossaryTerms = buildTerms();
export const publishedGlossaryTerms = allGlossaryTerms.filter((t) => !t.draft);
export const visibleGlossaryTerms = import.meta.env.PROD
  ? publishedGlossaryTerms
  : allGlossaryTerms;

export function getGlossaryTermBySlug(slug: string): GlossaryTerm | undefined {
  return visibleGlossaryTerms.find((t) => t.slug === slug);
}

export function glossaryCategories(): { category: string; terms: GlossaryTerm[] }[] {
  const byCategory = new Map<string, GlossaryTerm[]>();
  for (const term of visibleGlossaryTerms) {
    const bucket = byCategory.get(term.category);
    if (bucket) bucket.push(term);
    else byCategory.set(term.category, [term]);
  }
  return [...byCategory.entries()]
    .map(([category, terms]) => ({ category, terms }))
    .sort((a, b) => a.category.localeCompare(b.category));
}
